import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import { Calendar, ChevronLeft, ChevronRight, Search as SearchIcon } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { getReconciliationHistoryBatchColumns, getReconciliationTableColumns } from './statementTableColumns';
import { formatCurrency } from '../../helpers/global';
import AdvanceDateRangePicker from '../common/AdvanceDateRangePicker';
import ConfirmReconcileModal from './ConfirmReconcileModal';
import DataTable from '../common/data-table/DataTable';
import ReconciliationHistoryCard, { ReconciliationHistoryCardSkeleton } from './ReconciliationHistoryCard';
import ChipSkeletonLoader from '../common/ChipSkeletonLoader';

const defaultHistoryDateRange = {
  startDate: null,
  endDate: null,
  key: 'selection',
  rangeType: null
};

const DEFAULT_PENDING_SORT = { key: 'paidDate', direction: 'desc' };
const DEFAULT_HISTORY_SORT = { key: 'reconciledAt', direction: 'desc' };
const DEFAULT_PAGE_SIZE = 20;
const HISTORY_CARD_WIDTH = 320;
const HISTORY_CARD_GAP = 16;

const HISTORY_SCROLL_BUTTONS = [
  {
    key: 'left',
    direction: -1,
    canScrollKey: 'canScrollLeft',
    positionStyle: { left: 8 },
    Icon: ChevronLeft
  },
  {
    key: 'right',
    direction: 1,
    canScrollKey: 'canScrollRight',
    positionStyle: { right: 8 },
    Icon: ChevronRight
  }
];

const toReconciledDateParam = date => {
  if (!date) return '';
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const ReconciliationTab = ({ onTabCountsChange }) => {
  const [pendingReconciliationInvoices, setPendingReconciliationInvoices] = useState({
    statements: [],
    summary: { count: 0, totalAmount: 0, totalHardCost: 0, totalCollectedFee: 0 },
    loading: false,
    loadingMore: false,
    page: 1,
    totalCount: 0,
    hasMoreData: true,
    sort: DEFAULT_PENDING_SORT
  });

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [reconcileTargetIds, setReconcileTargetIds] = useState(new Set());
  const [ignoreTargetIds, setIgnoreTargetIds] = useState(new Set());

  const [modal, setModal] = useState({
    show: false,
    submitting: false
  });

  const [ignoreModal, setIgnoreModal] = useState({
    show: false,
    submitting: false
  });

  const [history, setHistory] = useState({
    batches: [],
    totalCount: 0,
    page: 1,
    loading: false,
    loadingMore: false,
    dateRange: defaultHistoryDateRange,
    sort: DEFAULT_HISTORY_SORT
  });

  const [batch, setBatch] = useState({
    expanded: null,
    invoices: {},
    pages: {},
    totalCounts: {},
    hasMoreData: {},
    loading: null,
    loadingMore: null
  });

  const abortControllerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const historyScrollRef = useRef(null);
  const expandedDataTableRef = useRef(null);

  const [historyScrollState, setHistoryScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false
  });

  const updateHistoryScrollState = useCallback(() => {
    const container = historyScrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    setHistoryScrollState({
      canScrollLeft: scrollLeft > 0,
      canScrollRight: scrollLeft + clientWidth < scrollWidth - 1
    });
  }, []);

  const scrollHistory = useCallback(direction => {
    historyScrollRef.current?.scrollBy({
      left: direction * 350,
      behavior: 'smooth'
    });
  }, []);

  const SUMMARY_CARDS = useMemo(
    () => [
      {
        label: 'Pending Invoices',
        value: pendingReconciliationInvoices.summary.count,
        description: 'Marked paid, not yet reconciled',
        color: 'var(--gray)'
      },
      {
        label: 'Total Invoice Amount',
        value: formatCurrency(pendingReconciliationInvoices.summary.totalAmount),
        description: 'Sum of all pending invoices',
        color: 'var(--info)'
      },
      {
        label: 'Working Capital',
        value: formatCurrency(pendingReconciliationInvoices.summary.totalHardCost),
        description: 'Keep in main account',
        color: 'var(--danger)'
      },
      {
        label: 'Collected Fees',
        value: formatCurrency(pendingReconciliationInvoices.summary.totalCollectedFee),
        description: 'Transfer to separate account',
        color: 'var(--success)'
      }
    ],
    [pendingReconciliationInvoices.summary]
  );

  const fetchReconciliation = useCallback(
    async ({ page = 1, append = false } = {}) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setPendingReconciliationInvoices(prev => ({
        ...prev,
        [append ? 'loadingMore' : 'loading']: true
      }));

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.INVOICE_STATEMENTS_RECONCILIATION,
        method: 'POST',
        requestBody: {
          search: searchQuery,
          sort: {
            [pendingReconciliationInvoices.sort.key]: pendingReconciliationInvoices.sort.direction === 'asc' ? 1 : -1
          },
          page,
          pageSize: DEFAULT_PAGE_SIZE
        },
        signal: controller.signal
      });

      if (controller.signal.aborted || abortControllerRef.current !== controller) {
        return;
      }

      if (error) {
        setPendingReconciliationInvoices(prev => ({
          ...prev,
          loading: false,
          loadingMore: false
        }));
        toast.error(error || 'Failed to load reconciliation statements');
        return;
      }

      const totalCount = response.totalCount || 0;
      const totalPages = Math.max(1, Math.ceil(totalCount / DEFAULT_PAGE_SIZE));

      setPendingReconciliationInvoices(prev => ({
        ...prev,
        loading: false,
        loadingMore: false,
        statements: append
          ? [...prev.statements, ...(response.statements || [])]
          : response.statements || [],
        summary: response.summary || { count: 0, totalAmount: 0, totalHardCost: 0, totalCollectedFee: 0 },
        totalCount,
        page,
        hasMoreData: page < totalPages
      }));

      onTabCountsChange?.({
        unpaidCount: response.unpaidCount,
        pendingReconciliationCount: response.pendingReconciliationCount
      });
    },
    [searchQuery, pendingReconciliationInvoices.sort, onTabCountsChange]
  );

  const fetchHistoryBatches = useCallback(
    async ({ page = 1, append = false } = {}) => {
      setHistory(prev => ({ ...prev, [append ? 'loadingMore' : 'loading']: true }));

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.INVOICE_STATEMENTS_RECONCILIATION_HISTORY,
        method: 'POST',
        requestBody: {
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          sort:
            history.sort && history.sort.key
              ? { [history.sort.key]: history.sort.direction === 'asc' ? 1 : -1 }
              : undefined,
          reconciledDateFrom: toReconciledDateParam(history.dateRange.startDate),
          reconciledDateTo: toReconciledDateParam(history.dateRange.endDate)
        }
      });

      setHistory(prev => ({ ...prev, [append ? 'loadingMore' : 'loading']: false }));

      if (error) {
        toast.error(error || 'Failed to load reconciliation history');
        return;
      }

      setHistory(prev => ({
        ...prev,
        batches: append ? [...prev.batches, ...(response.batches || [])] : response.batches || [],
        totalCount: response.totalCount || 0
      }));
    },
    [history.sort, history.dateRange]
  );

  const fetchBatchHistoryInvoices = useCallback(
    async (batchId, { page = 1, append = false } = {}) => {
      setBatch(prev => ({
        ...prev,
        loading: append ? prev.loading : batchId,
        loadingMore: append ? batchId : null
      }));

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.INVOICE_STATEMENTS_RECONCILIATION_HISTORY_BATCH(batchId),
        method: 'POST',
        requestBody: {
          sort: { [history.sort.key]: history.sort.direction === 'asc' ? 1 : -1 },
          page,
          pageSize: DEFAULT_PAGE_SIZE
        }
      });

      if (error) {
        setBatch(prev => ({ ...prev, loading: null, loadingMore: null }));
        toast.error(error || 'Failed to load batch invoices');
        return;
      }

      const totalCount = response.totalCount || 0;
      const totalPages = Math.max(1, Math.ceil(totalCount / DEFAULT_PAGE_SIZE));

      setBatch(prev => ({
        ...prev,
        loading: null,
        loadingMore: null,
        invoices: {
          ...prev.invoices,
          [batchId]: append
            ? [...(prev.invoices[batchId] || []), ...(response.invoices || [])]
            : response.invoices || []
        },
        pages: { ...prev.pages, [batchId]: page },
        totalCounts: { ...prev.totalCounts, [batchId]: totalCount },
        hasMoreData: { ...prev.hasMoreData, [batchId]: page < totalPages }
      }));
    },
    [history.sort]
  );

  useEffect(() => {
    setPendingReconciliationInvoices(prev => ({ ...prev, page: 1, hasMoreData: true }));
    fetchReconciliation({ page: 1, append: false });
  }, [fetchReconciliation]);

  useEffect(() => {
    fetchHistoryBatches({ page: history.page, append: history.page > 1 });
  }, [fetchHistoryBatches, history.page]);

  useEffect(() => {
    const container = historyScrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const scrollRight = scrollWidth - (scrollLeft + clientWidth);

      updateHistoryScrollState();

      if (scrollRight < 200 && !history.loadingMore && !history.loading) {
        if (history.batches.length < history.totalCount) {
          setHistory(prev => ({ ...prev, page: prev.page + 1 }));
        }
      }
    };

    container.addEventListener('scroll', handleScroll);
    updateHistoryScrollState();

    return () => container.removeEventListener('scroll', handleScroll);
  }, [history.loadingMore, history.loading, history.batches.length, history.totalCount, updateHistoryScrollState]);

  useEffect(() => {
    const container = historyScrollRef.current;
    if (!container || history.loadingMore || history.loading) return;

    const { scrollWidth, clientWidth } = container;
    const isScrollable = scrollWidth > clientWidth;
    const hasMoreData = history.batches.length < history.totalCount;

    if (!isScrollable && hasMoreData && history.batches.length > 0) {
      setHistory(prev => ({ ...prev, page: prev.page + 1 }));
    }
  }, [history.batches, history.totalCount, history.loadingMore, history.loading]);

  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(value.trim());
      setPendingReconciliationInvoices(prev => ({ ...prev, page: 1, hasMoreData: true }));
    }, 500);
  };

  const handlePendingSortChange = useCallback(newSort => {
    setPendingReconciliationInvoices(prev => ({ ...prev, sort: newSort, page: 1, hasMoreData: true }));
  }, []);

  const handlePendingLoadMore = useCallback(() => {
    const { loading, loadingMore, hasMoreData, page } = pendingReconciliationInvoices;
    if (loading || loadingMore || !hasMoreData) return;
    fetchReconciliation({ page: page + 1, append: true });
  }, [pendingReconciliationInvoices, fetchReconciliation]);

  const handleHistorySortChange = useCallback(
    newSort => {
      setHistory(prev => ({ ...prev, sort: newSort, page: 1 }));
      if (batch.expanded) {
        fetchBatchHistoryInvoices(batch.expanded, { page: 1 });
      }
    },
    [batch.expanded, fetchBatchHistoryInvoices]
  );

  const handleBatchLoadMore = useCallback(() => {
    const batchId = batch.expanded;
    if (!batchId) return;
    if (batch.loading === batchId || batch.loadingMore === batchId) return;
    if (!batch.hasMoreData[batchId]) return;
    const nextPage = (batch.pages[batchId] || 1) + 1;
    fetchBatchHistoryInvoices(batchId, { page: nextPage, append: true });
  }, [batch, fetchBatchHistoryInvoices]);

  const openReconcileModal = () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one invoice to reconcile');
      return;
    }
    setReconcileTargetIds(new Set(selectedIds));
    setModal({ show: true, submitting: false });
  };

  const openIgnoreModal = (ids = selectedIds) => {
    const targetIds = ids instanceof Set ? ids : new Set(ids);
    if (targetIds.size === 0) {
      toast.error('Please select at least one invoice to ignore');
      return;
    }
    setIgnoreTargetIds(targetIds);
    setIgnoreModal({ show: true, submitting: false });
  };

  const handleReconcile = async () => {
    setModal(prev => ({ ...prev, submitting: true }));

    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.INVOICE_STATEMENTS_RECONCILE,
      method: 'POST',
      requestBody: { statementIds: Array.from(reconcileTargetIds) }
    });

    setModal(prev => ({ ...prev, submitting: false }));

    if (error) {
      toast.error(error || 'Failed to reconcile statements');
      return;
    }

    toast.success(`${reconcileTargetIds.size} invoice(s) reconciled successfully`);
    const reconciledIds = new Set(reconcileTargetIds);
    setModal({ show: false, submitting: false });
    setSelectedIds(new Set());
    setReconcileTargetIds(new Set());
    setPendingReconciliationInvoices(prev => ({
      ...prev,
      page: 1,
      hasMoreData: true,
      statements: prev.statements.filter(s => !reconciledIds.has(s._id)),
      totalCount: Math.max(0, prev.totalCount - reconciledIds.size)
    }));
    setHistory(prev => ({ ...prev, page: 1 }));
    setBatch({
      expanded: null,
      invoices: {},
      pages: {},
      totalCounts: {},
      hasMoreData: {},
      loading: null,
      loadingMore: null
    });
    fetchReconciliation({ page: 1, append: false });
    fetchHistoryBatches({ page: 1 });
  };

  const handleIgnore = async () => {
    setIgnoreModal(prev => ({ ...prev, submitting: true }));

    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.INVOICE_STATEMENTS_RECONCILIATION_IGNORE,
      method: 'POST',
      requestBody: { statementIds: Array.from(ignoreTargetIds) }
    });

    setIgnoreModal(prev => ({ ...prev, submitting: false }));

    if (error) {
      toast.error(error || 'Failed to ignore statements');
      return;
    }

    toast.success(`${ignoreTargetIds.size} invoice(s) ignored`);
    const ignoredIds = new Set(ignoreTargetIds);
    setIgnoreModal({ show: false, submitting: false });
    setSelectedIds(new Set());
    setIgnoreTargetIds(new Set());
    setPendingReconciliationInvoices(prev => ({
      ...prev,
      page: 1,
      hasMoreData: true,
      statements: prev.statements.filter(s => !ignoredIds.has(s._id)),
      totalCount: Math.max(0, prev.totalCount - ignoredIds.size)
    }));
    fetchReconciliation({ page: 1, append: false });
  };

  const selectedStatements = pendingReconciliationInvoices.statements.filter(s => selectedIds.has(s._id));
  const selectedTotalAmount = selectedStatements.reduce((sum, s) => sum + (parseFloat(s.invoiceAmount) || 0), 0);
  const selectedTotalHardCost = selectedStatements.reduce((sum, s) => sum + (parseFloat(s.hardCost) || 0), 0);
  const selectedTotalCollectedFee = selectedStatements.reduce((sum, s) => sum + (parseFloat(s.collectedFee) || 0), 0);

  const reconcileStatements = pendingReconciliationInvoices.statements.filter(s => reconcileTargetIds.has(s._id));
  const reconcileTotalHardCost = reconcileStatements.reduce((sum, s) => sum + (parseFloat(s.hardCost) || 0), 0);
  const reconcileTotalCollectedFee = reconcileStatements.reduce((sum, s) => sum + (parseFloat(s.collectedFee) || 0), 0);

  const ignoreStatements = pendingReconciliationInvoices.statements.filter(s => ignoreTargetIds.has(s._id));
  const ignoreTotalHardCost = ignoreStatements.reduce((sum, s) => sum + (parseFloat(s.hardCost) || 0), 0);
  const ignoreTotalCollectedFee = ignoreStatements.reduce((sum, s) => sum + (parseFloat(s.collectedFee) || 0), 0);

  const pendingReconciliationTableColumns = useMemo(
    () =>
      getReconciliationTableColumns({
        onReconcileClick: rowId => {
          setReconcileTargetIds(new Set([rowId]));
          setModal({ show: true, submitting: false });
        },
        onIgnoreClick: rowId => {
          openIgnoreModal(new Set([rowId]));
        }
      }),
    []
  );

  const historyBatchColumns = useMemo(() => getReconciliationHistoryBatchColumns(false), []);

  const toggleBatchDetail = batchId => {
    if (batch.expanded === batchId) {
      setBatch(prev => ({ ...prev, expanded: null }));
    } else {
      setBatch(prev => ({ ...prev, expanded: batchId }));
      fetchBatchHistoryInvoices(batchId, { page: 1 });

      setTimeout(() => {
        if (expandedDataTableRef.current) {
          expandedDataTableRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h6 className="my-3 font-weight-bold xxxlargeFont">Financial Reconciliation</h6>
        <p className="text-muted mb-0 smallFont">
          Pending invoices awaiting reconciliation — confirm deposit in QB and transfer collected fees.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="row">
        {SUMMARY_CARDS.map((item, index) => (
          <div key={index} className="col-12 col-md-6 col-lg-3 mb-4 ">
            <Card className="h-100 border-none shadow-sm" style={{ borderLeft: `3px solid ${item.color}` }}>
              <Card.Body className="p-3">
                <div className="d-flex flex-column justify-content-between h-100">
                  <div className="text-muted text-uppercase tinyFont fw-bold">{item.label}</div>

                  <div className="xxxlargeFont fw-bold" style={{ color: item.color }}>
                    {!searchQuery && pendingReconciliationInvoices.loading ? (
                      <div className="my-2">
                        <ChipSkeletonLoader chipHeight={24} chipWidths={[100]} ariaLabel="Loading statement summary" />
                      </div>
                    ) : (
                      item.value
                    )}
                  </div>

                  <div className="text-muted xsmallFont">{item.description}</div>
                </div>
              </Card.Body>
            </Card>
          </div>
        ))}
      </div>

      {/* Batch Action Bar */}
      {selectedIds.size > 0 && (
        <div
          className="d-flex align-items-center justify-content-between px-3 py-2 mb-4 shadow-sm bg-cyan-soft text-white"
          style={{
            background: '#1E293B',
            color: 'white',
            borderRadius: 8,
            position: 'sticky',
            top: 72,
            zIndex: 999
          }}
        >
          <div className="d-flex align-items-center smallFont" style={{ gap: 12 }}>
            <span className="font-weight-bold">{selectedIds.size} invoices selected</span>
            <span>Total: {formatCurrency(selectedTotalAmount)}</span>
            <span>Hard Cost: {formatCurrency(selectedTotalHardCost)}</span>
            <span>Collected: {formatCurrency(selectedTotalCollectedFee)}</span>
          </div>
          <div className="d-flex" style={{ gap: 8 }}>
            <Button size="sm" variant="outline-light" onClick={() => setSelectedIds(new Set())} className="fw-bold">
              Clear Selection
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => openIgnoreModal()}
              className="fw-bold border-0"
            >
              Ignore Selected
            </Button>
            <Button size="sm" onClick={openReconcileModal} className="hover-cyan-soft border-0 text-white fw-bold">
              Confirm Reconciliation
            </Button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-3">
        <div className="position-relative">
          <SearchIcon
            className="text-muted"
            size={16}
            style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }}
          />
          <Form.Control
            placeholder="Search by invoice number, file number, or address..."
            value={searchInput}
            onChange={handleSearchChange}
            style={{ paddingLeft: 36 }}
            className="bg-white"
          />
        </div>
      </div>

      {/* Pending Reconciliation Table */}
      <div className="mb-4">
        <DataTable
          key={`${pendingReconciliationInvoices.sort.key}-${pendingReconciliationInvoices.sort.direction}`}
          data={pendingReconciliationInvoices.statements}
          columns={pendingReconciliationTableColumns}
          rowKey="_id"
          selectable
          checkboxColumn
          selectedRows={Array.from(selectedIds)}
          onSelectionChange={newSelection => setSelectedIds(new Set(newSelection))}
          onSelectAll={newSelection => setSelectedIds(new Set(newSelection))}
          sortable
          defaultSort={pendingReconciliationInvoices.sort}
          onSortChange={handlePendingSortChange}
          serverSort
          pagination={false}
          loading={pendingReconciliationInvoices.loading}
          hover
          maxHeight="calc(100vh - 600px)"
          emptyMessage="No invoices pending reconciliation"
          onBottomReached={handlePendingLoadMore}
          loadingMoreData={pendingReconciliationInvoices.loadingMore}
          bottomOffset={100}
        />
        {pendingReconciliationInvoices.totalCount > 0 && !pendingReconciliationInvoices.loading && (
          <div className="text-muted mt-2">
            Showing <span className="text-dark font-weight-bold">{pendingReconciliationInvoices.totalCount}</span>{' '}
            invoices pending reconciliation
          </div>
        )}
      </div>

      {/* History Section */}
      <div className="mt-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="font-weight-bold mb-0">
            <Calendar size={20} className="mr-2" />
            Reconciled History
          </h5>
          <div style={{ width: 300 }}>
            <AdvanceDateRangePicker
              className="w-100"
              value={history.dateRange}
              onChange={ranges => {
                const { startDate, endDate, ...rest } = ranges.selection;
                setHistory(prev => ({
                  ...prev,
                  dateRange: {
                    ...rest,
                    key: 'selection',
                    startDate: startDate && moment(startDate).isValid() ? startDate : null,
                    endDate: endDate && moment(endDate).isValid() ? endDate : null
                  },
                  page: 1
                }));
                setBatch({
                  expanded: null,
                  invoices: {},
                  pages: {},
                  totalCounts: {},
                  hasMoreData: {},
                  loading: null,
                  loadingMore: null
                });
              }}
              rangeType={history.dateRange.rangeType}
              isFloating={true}
              extraStyleForSlideBarFilter={true}
              showNavigator={false}
              topOffset={0}
              allowAllTime={false}
              singleDateMode={false}
            />
          </div>
        </div>

        {history.loading ? (
          <div className="mb-4">
            <ReconciliationHistoryCardSkeleton cardCount={3} />
          </div>
        ) : history.batches.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center py-5 text-muted">
              <p className="mb-0">No reconciliation history found</p>
            </Card.Body>
          </Card>
        ) : (
          <>
            <div className="position-relative mb-3">
              {HISTORY_SCROLL_BUTTONS.filter(({ canScrollKey }) => historyScrollState[canScrollKey]).map(
                ({ key, direction, positionStyle, Icon }) => (
                  <Button
                    key={key}
                    variant="link"
                    className="position-absolute d-flex align-items-center justify-content-center border-0 rounded-circle p-0 shadow-sm"
                    style={{
                      ...positionStyle,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 50,
                      width: 38,
                      height: 38,
                      background: 'rgba(0, 0, 0, 0.45)',
                      transition: 'background-color 150ms ease'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(0,0,0,0.6)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'rgba(0,0,0,0.45)';
                    }}
                    onClick={() => scrollHistory(direction)}
                  >
                    <Icon size={18} className="text-white flex-shrink-0" />
                  </Button>
                )
              )}

              <div
                ref={historyScrollRef}
                className="hide-scrollbar mb-2"
                style={{ overflowX: 'auto', overflowY: 'hidden' }}
              >
                <div className="d-flex flex-nowrap" style={{ gap: HISTORY_CARD_GAP, minWidth: 'min-content' }}>
                  {history.batches.map(batchItem => (
                    <div key={batchItem._id} className="flex-shrink-0" style={{ width: HISTORY_CARD_WIDTH }}>
                      <ReconciliationHistoryCard
                        batch={batchItem}
                        expanded={batch.expanded === batchItem._id}
                        onClick={() => toggleBatchDetail(batchItem._id)}
                      />
                    </div>
                  ))}

                  {history.loadingMore && <ReconciliationHistoryCardSkeleton cardCount={1} />}
                </div>
              </div>
            </div>

            {batch.expanded && (
              <div ref={expandedDataTableRef} className="mb-4">
                <DataTable
                  key={`${batch.expanded}-${history.sort.key}-${history.sort.direction}`}
                  data={batch.invoices[batch.expanded] || []}
                  columns={historyBatchColumns}
                  rowKey="statementId"
                  sortable
                  defaultSort={history.sort}
                  onSortChange={handleHistorySortChange}
                  serverSort
                  pagination={false}
                  loading={batch.loading === batch.expanded}
                  hover
                  maxHeight="calc(100vh - 600px)"
                  emptyMessage="No invoices in this batch"
                  onBottomReached={handleBatchLoadMore}
                  loadingMoreData={batch.loadingMore === batch.expanded}
                  bottomOffset={100}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmReconcileModal
        show={modal.show}
        onHide={() => {
          if (!modal.submitting) {
            setModal({ show: false, submitting: false });
            setReconcileTargetIds(new Set());
          }
        }}
        onSubmit={handleReconcile}
        submitting={modal.submitting}
        invoiceCount={reconcileTargetIds.size}
        totalHardCost={reconcileTotalHardCost}
        totalCollectedFee={reconcileTotalCollectedFee}
      />

      <ConfirmReconcileModal
        show={ignoreModal.show}
        mode="ignore"
        onHide={() => {
          if (!ignoreModal.submitting) {
            setIgnoreModal({ show: false, submitting: false });
            setIgnoreTargetIds(new Set());
          }
        }}
        onSubmit={handleIgnore}
        submitting={ignoreModal.submitting}
        invoiceCount={ignoreTargetIds.size}
        totalHardCost={ignoreTotalHardCost}
        totalCollectedFee={ignoreTotalCollectedFee}
      />
    </div>
  );
};

export default ReconciliationTab;
